import React from 'react';
import { useTranslation } from 'react-i18next';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Doughnut } from 'react-chartjs-2';
import { useBudget } from '../../context/BudgetContext';
import { formatCAD } from '../../utils/formatCurrency';

export default function EmergencyFundCard() {
  const { t } = useTranslation();
  const { state, derived } = useBudget();

  const targetMonths = state.goals.emergencyFundMonths || 3;
  const targetAmount = derived.monthlyTotalExpenses * targetMonths;
  const currentSavings = Math.max(0, derived.monthlyNet * targetMonths);
  const progress = targetAmount > 0 ? Math.min(100, (currentSavings / targetAmount) * 100) : 0;
  const remaining = Math.max(0, targetAmount - currentSavings);

  const data = {
    datasets: [{
      data: [progress, 100 - progress],
      backgroundColor: ['#2E7D32', '#E6E9EE'],
      borderWidth: 0,
      cutout: '75%',
    }],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
  };

  return (
    <Card elevation={1} sx={{ height: '100%', border: '1px solid #E6E9EE', transition: 'transform 0.2s ease, box-shadow 0.2s ease', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } }}>
      <CardContent sx={{ textAlign: 'center', py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.25, color: '#1E3A5F' }}>
          {t('budgetPlanner.goals.emergencyCard.title', 'Fondo de emergencia')}
        </Typography>

        <Box sx={{ position: 'relative', width: 120, height: 120, mx: 'auto', mb: 1.5 }}>
          <Doughnut data={data} options={options} />
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}
          >
            <Typography variant="h6" fontWeight={700} color="#2E7D32">
              {Math.round(progress)}%
            </Typography>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary">
          {t('budgetPlanner.goals.emergencyCard.target', 'Meta')}: {formatCAD(targetAmount)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('budgetPlanner.goals.emergencyCard.targetMonths', '{{months}} meses de gastos', { months: targetMonths })}
        </Typography>
        {remaining > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {t('budgetPlanner.goals.emergencyCard.remaining', 'Faltan {{amount}}', { amount: formatCAD(remaining) })}
          </Typography>
        )}

        {/* Micro-insights */}
        <Box sx={{ mt: 1.5 }}>
          {remaining > 0 ? (
            <>
              <Typography sx={{ fontSize: 12, color: '#5F6B7A', lineHeight: 1.5 }}>
                Te faltan {formatCAD(remaining)} para cubrir {targetMonths} meses de gastos.
              </Typography>
              {derived.monthlyNet > 0 && (
                <Typography sx={{ fontSize: 12, color: '#5F6B7A', lineHeight: 1.5 }}>
                  A tu ritmo actual, lo completarías en ~{Math.ceil(remaining / derived.monthlyNet)} meses.
                </Typography>
              )}
            </>
          ) : (
            <Typography sx={{ fontSize: 12, color: '#5F6B7A', lineHeight: 1.5 }}>
              Ya tienes cubierto tu fondo de emergencia.
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
